// The product editor's fields (docs/claude/products.md, *Editing a product*). The
// editor edits the stored product in place: it lists the fields the entry has,
// each with its own control, offers the known ones it lacks, and removes what is
// removed — nothing else changes. This module knows every field: how its control
// looks, where it sits, what adding it gives and whether it may go. Pure and
// DOM-free; main.ts draws the controls.

import type {
  Connection,
  ConnectionType,
  ConnPort,
  Lang,
  Preparation,
  ProductPart,
  ProductTemplate,
  ProductVariant,
  PropertyDef,
  RoomWallCut,
  SkirtingSpec,
  Vec2,
  WallCut,
  WallRisers,
} from '../shared/types';
import { CONNECTION_TYPES } from '../shared/types';
import { DOSER_MODELS } from '../shared/doserSolids';
import { DUCT_FAN_MODELS } from '../shared/ductFans';
import { HOT_TUB_MODELS } from './hotTub';
import { bodyFields } from './catalogueCopy';
import {
  isElecRoom,
  preparationTypes,
} from './elecSchematic';
import { dictDefaults } from '../shared/names';

/** How a field's control looks. */
export type FieldKind =
  | 'bool'
  | 'category'
  | 'color'
  | 'drawing'
  | 'i18n'
  | 'list'
  | 'longText'
  | 'mm'
  | 'mmList'
  | 'number'
  | 'object'
  | 'partRef'
  | 'portX'
  | 'portY'
  | 'power'
  | 'propValue'
  | 'select'
  | 'signedMm'
  | 'text';

/** The objects the editor lists the fields of. */
export type ItemKind =
  | 'airInlet'
  | 'connection'
  | 'gap'
  | 'load'
  | 'part'
  | 'point'
  | 'port'
  | 'preparation'
  | 'product'
  | 'property'
  | 'roomWallCut'
  | 'screw'
  | 'skirting'
  | 'tiers'
  | 'variant'
  | 'wallCut'
  | 'wallRisers';

/** The sections a node's fields are listed in, in order; fields this build does
 *  not know come last. */
export const SECTIONS = [
  'General',
  'Size',
  'Drawing',
  'Placement',
  'Heater body',
  'Heater gap',
  'Heater cover',
  'Bench heights',
  'Bench',
  'Lighting',
  'Power',
  'Hot tub',
  'Wall',
  'Repeatable',
  'Properties',
  'Variants',
  'Preparations',
  'Connection points',
  'Connections',
  'Other fields',
] as const;
export type Section = (typeof SECTIONS)[number];

export interface FieldSpec {
  /** English: the DICT key it is shown by. */
  label: string;
  /** Its label on this object, where what it means depends on another field. */
  labelOf?: (owner: Record<string, unknown>) => string;
  kind: FieldKind;
  section: Section;
  /** A plain number's unit, shown after it (W, W/m, °, lm). */
  unit?: string;
  options?: ReadonlyArray<{ value: string; label: string }>;
  /** Its options where they depend on the object, or on whether the product
   *  is a room (`fieldOptions`). */
  optionsOf?: (obj: Record<string, unknown>, room: boolean) => ReadonlyArray<{ value: string; label: string }>;
  /** What "Add field…" gives it. */
  make: () => unknown;
  /** The object cannot be without it: edited, never removed. */
  required?: boolean;
  /** A list's items, an object's own fields. */
  item?: ItemKind;
  /** The keys its control edits with it (a name's translations, a drawing's
   *  source file): removed with it, never listed on their own. */
  companions?: readonly string[];
  /** Whether "Add field…" offers it, from the object's other values. */
  applies?: (owner: Record<string, unknown>) => boolean;
  /** Computed from the object's other values, so never listed: a multi-part
   *  product's width and depth. */
  derived?: (owner: Record<string, unknown>) => boolean;
}

/** A known key: a field, part of another field's control (`companion`), never
 *  shown (`hidden`: an id, the built-in flag), or shown by its value's type
 *  (`generic`). */
type Slot =
  | 'companion'
  | 'generic'
  | 'hidden'
  | FieldSpec;
type Table<T> = { readonly [K in keyof Required<T>]: Slot };

const noParts = (o: Record<string, unknown>): boolean => !(Array.isArray(o.parts) && o.parts.length);
const hasParts = (o: Record<string, unknown>): boolean => !noParts(o);
const noModel = (o: Record<string, unknown>): boolean => o.model3d === undefined;
const discipline = (t: ConnectionType): { value: string; label: string } => ({ value: t, label: t[0].toUpperCase() + t.slice(1) });
const disciplines = CONNECTION_TYPES.map(discipline);
const mm = (label: string, section: Section, extra: Partial<FieldSpec> = {}): FieldSpec => ({ label, kind: 'mm', section, make: () => 0, ...extra });
const num = (label: string, section: Section, unit: string, extra: Partial<FieldSpec> = {}): FieldSpec => ({ label, kind: 'number', section, unit, make: () => 0, ...extra });
const bool = (label: string, section: Section): FieldSpec => ({ label, kind: 'bool', section, make: () => true });
const drawing: FieldSpec = { label: 'Drawing', kind: 'drawing', section: 'Drawing', companions: ['dxf', 'imageDataUrl', 'fileName'], make: () => undefined };
const gap = (label: string, section: Section): FieldSpec => ({ label, kind: 'object', section, item: 'gap', make: () => ({}) });
const wallCut = (label: string, item: 'roomWallCut' | 'wallCut'): FieldSpec => ({ label, kind: 'object', section: 'Wall', item, make: (): WallCut => ({ width: 100, height: 100 }) });
const wallRisers: FieldSpec = { label: 'Hot and cold risers in a room’s wall', kind: 'object', section: 'Wall', item: 'wallRisers', make: (): WallRisers => ({ x: 0, z: 0, spacing: 100, depth: 57, above: 100, diameter: 20 }) };
const benchRest: FieldSpec = { label: 'On the benches', kind: 'select', section: 'Placement', make: () => 'above', options: [{ value: 'above', label: 'Rests on the seat under it' }] };
const tiers: FieldSpec = { label: 'Bench heights', kind: 'object', section: 'Bench heights', item: 'tiers', make: () => ({ low: 450, medium: 650, high: 850 }) };
const list = (label: string, section: Section, item: ItemKind, extra: Partial<FieldSpec> = {}): FieldSpec => ({ label, kind: 'list', section, item, make: () => [], ...extra });
const doserModel: FieldSpec = { label: 'Model', kind: 'select', section: 'General', make: () => DOSER_MODELS[0], options: DOSER_MODELS.map((k) => ({ value: k, label: k })) };

const PRODUCT: Table<ProductTemplate> = {
  id: 'hidden',
  builtin: 'hidden',
  name: { label: 'Name', kind: 'i18n', section: 'General', companions: ['nameI18n'], required: true, make: () => '' },
  nameI18n: 'companion',
  category: { label: 'Category', kind: 'category', section: 'General', required: true, make: () => 'custom' },
  description: { label: 'Description', kind: 'longText', section: 'General', make: () => '' },
  partName: { label: 'Part name', kind: 'text', section: 'General', make: () => '', applies: noParts },
  color: { label: 'Colour', kind: 'color', section: 'General', required: true, make: () => '#cdd6e0' },
  lockResize: bool('Lock resize', 'General'),
  electrical: bool('Electrical (part)', 'General'),
  junctionBox: bool('Junction box', 'General'),
  autoBenches: bool('Builds its benches', 'General'),
  polyRoom: bool('Polygonal room', 'General'),
  width: mm('Width (mm)', 'Size', { required: true, applies: noParts, derived: hasParts }),
  depth: mm('Depth (mm)', 'Size', { required: true, applies: noParts, derived: hasParts }),
  height: mm('Height (mm)', 'Size'),
  geometry: drawing,
  dxf: 'companion',
  imageDataUrl: 'companion',
  fileName: 'companion',
  heightFromGround: { label: 'Height from ground (mm)', kind: 'signedMm', section: 'Placement', make: () => 0 },
  noBenchCarve: bool('Leaves benches whole', 'Placement'),
  onRoomTop: bool('Sits on a room’s ceiling', 'Placement'),
  underCeiling: bool('Hangs under a room’s ceiling', 'Placement'),
  wallMountCentre: mm('Centre height on a wall (mm)', 'Placement', { make: () => 1500 }),
  wallMountBelowCeiling: mm('Centre below the ceiling on a wall (mm)', 'Placement', { make: () => 200 }),
  wallMountUnderside: bool('Stands on a wall by its underside', 'Placement'),
  inWall: mm('Set into a wall: front out of it (mm)', 'Wall', { make: () => 5 }),
  backOnWall: bool('Stands with its back on a wall', 'Wall'),
  wallCut: wallCut('Cut in a building wall', 'wallCut'),
  roomWallCut: wallCut('Cut in a room’s wall', 'roomWallCut'),
  wallRisers: wallRisers,
  benchRest: benchRest,
  cutsBackrest: bool('Interrupts the backrest', 'Wall'),
  heaterHeight: mm('Body height (mm)', 'Heater body'),
  heaterGap: gap('Heater gap', 'Heater gap'),
  heaterCover: gap('Heater cover', 'Heater cover'),
  heaterGuardColor: { label: 'Cover colour', kind: 'color', section: 'Heater cover', make: () => '#8a6a4a' },
  benchTierHeights: tiers,
  slats: { label: 'Slats (mm)', kind: 'mmList', section: 'Bench', make: () => [90] },
  gaps: { label: 'Gaps (mm)', kind: 'mmList', section: 'Bench', make: () => [] },
  slatThickness: { label: 'Slat thickness (mm)', kind: 'mmList', section: 'Bench', make: () => [28] },
  overlap: mm('Overlap (mm)', 'Bench'),
  skirting: bool('Skirting', 'Bench'),
  skirtingBoards: { label: 'Skirting boards', kind: 'object', section: 'Bench', item: 'skirting', make: (): SkirtingSpec => ({ boards: [90], gaps: [], thickness: [20], clearance: 0 }) },
  lightKind: {
    label: 'Light', kind: 'select', section: 'Lighting', make: () => 'spot',
    options: [{ value: 'strip', label: 'LED strip' }, { value: 'spot', label: 'Spot light' }, { value: 'projector', label: 'Fibre projector' }],
  },
  lightColour: { label: 'Colour', kind: 'select', section: 'Lighting', make: () => '2700K', options: [{ value: '2700K', label: '2700 K white' }, { value: 'RGB', label: 'RGB' }] },
  lightPower: num('Power (W)', 'Lighting', 'W'),
  lightBeamAngle: num('Beam angle (°)', 'Lighting', '°', { make: () => 36 }),
  lightLumens: num('Luminous flux (lm)', 'Lighting', 'lm', { make: () => 250, labelOf: (o) => (o.lightKind === 'strip' ? 'Luminous flux per metre (lm/m)' : 'Luminous flux (lm)') }),
  powerPerMeter: num('Power per running meter (W/m)', 'Power', 'W/m'),
  powerPerSqm: num('Power per square meter (W/m²)', 'Power', 'W/m²'),
  hotTubModel: { label: 'Model', kind: 'select', section: 'Hot tub', make: () => 'virtus', options: Object.keys(HOT_TUB_MODELS).map((k) => ({ value: k, label: k })) },
  doserModel: doserModel,
  fanModel: { label: 'Model', kind: 'select', section: 'General', make: () => DUCT_FAN_MODELS[0], options: DUCT_FAN_MODELS.map((k) => ({ value: k, label: k })) },
  // Stored with the product, never edited: kept as it is on a save.
  model3d: 'hidden',
  throughWall: mm('Room side through the wall (mm)', 'Wall'),
  installScrews: list('Install screws', 'Wall', 'screw'),
  airInlet: { label: 'Air inlet', kind: 'object', section: 'Wall', item: 'airInlet', make: () => ({ widthMm: 100, heightMm: 100 }) },
  repeatable: bool('Repeatable', 'Repeatable'),
  offset: mm('Offset (mm)', 'Repeatable'),
  parts: 'hidden',
  properties: list('Properties', 'Properties', 'property', { required: true }),
  preparations: list('Preparations', 'Preparations', 'preparation'),
  ports: list('Connection points', 'Connection points', 'port', { applies: noModel }),
  connections: list('Connections', 'Connections', 'connection', { applies: hasParts }),
  variants: list('Variants', 'Variants', 'variant'),
};

const PART: Table<ProductPart> = {
  id: 'hidden',
  name: { label: 'Name', kind: 'i18n', section: 'General', companions: ['nameI18n'], required: true, make: () => '' },
  nameI18n: 'companion',
  color: { label: 'Colour', kind: 'color', section: 'General', make: () => '#cdd6e0' },
  width: mm('Width (mm)', 'Size', { required: true }),
  depth: mm('Depth (mm)', 'Size', { required: true }),
  height: mm('Height (mm)', 'Size'),
  doserModel,
  model3d: 'hidden',
  offset: { label: 'Position in the product (mm)', kind: 'object', section: 'Placement', item: 'point', required: true, make: (): Vec2 => ({ x: 0, y: 0 }) },
  rotation: num('Rotation (°)', 'Placement', '°'),
  geometry: drawing,
  dxf: 'companion',
  imageDataUrl: 'companion',
  fileName: 'companion',
  heightFromGround: { label: 'Height from ground (mm)', kind: 'signedMm', section: 'Placement', make: () => 0 },
  heaterGap: gap('Heater gap', 'Heater gap'),
  heaterCover: gap('Heater cover', 'Heater cover'),
  heaterCoverColor: { label: 'Cover colour', kind: 'color', section: 'Heater cover', make: () => '#8a6a4a' },
  benchTierHeights: tiers,
  powerPerMeter: num('Power per running meter (W/m)', 'Power', 'W/m'),
  powerPerSqm: num('Power per square meter (W/m²)', 'Power', 'W/m²'),
  onRoomTop: bool('Sits on a room’s ceiling', 'Placement'),
  underCeiling: bool('Hangs under a room’s ceiling', 'Placement'),
  wallMountCentre: mm('Centre height on a wall (mm)', 'Placement', { make: () => 1500 }),
  wallMountBelowCeiling: mm('Centre below the ceiling on a wall (mm)', 'Placement', { make: () => 200 }),
  wallMountUnderside: bool('Stands on a wall by its underside', 'Placement'),
  inWall: mm('Set into a wall: front out of it (mm)', 'Wall', { make: () => 5 }),
  backOnWall: bool('Stands with its back on a wall', 'Wall'),
  wallCut: wallCut('Cut in a building wall', 'wallCut'),
  roomWallCut: wallCut('Cut in a room’s wall', 'roomWallCut'),
  wallRisers: wallRisers,
  benchRest: benchRest,
  cutsBackrest: bool('Interrupts the backrest', 'Wall'),
  properties: list('Properties', 'Properties', 'property'),
  preparations: list('Preparations', 'Preparations', 'preparation'),
  ports: list('Connection points', 'Connection points', 'port', { applies: noModel }),
};

const PROPERTY: Table<PropertyDef> = {
  label: { label: 'Label', kind: 'text', section: 'General', required: true, make: () => '' },
  value: { label: 'Value', kind: 'propValue', section: 'General', required: true, make: () => '' },
  unit: { label: 'Unit', kind: 'text', section: 'General', make: () => '' },
  key: { label: 'Key', kind: 'text', section: 'General', required: true, make: () => '' },
};

const PREPARATION: Table<Preparation> = {
  id: 'hidden',
  // A room's: no electrical one, but one it has (the Preparations tab's rule).
  type: {
    label: 'Discipline', kind: 'select', section: 'General', required: true, options: disciplines, make: () => 'electrical',
    optionsOf: (o, room) => preparationTypes(room, o.type as ConnectionType).map(discipline),
  },
  label: { label: 'Description', kind: 'i18n', section: 'General', companions: ['labelI18n'], required: true, make: () => '' },
  labelI18n: 'companion',
};

const PORT: Table<ConnPort> = {
  id: 'hidden',
  name: { label: 'Name', kind: 'text', section: 'General', required: true, make: () => '' },
  type: { label: 'Discipline', kind: 'select', section: 'General', options: disciplines, make: () => 'electrical' },
  nx: { label: 'X from the centre (mm)', kind: 'portX', section: 'General', required: true, make: () => 0 },
  ny: { label: 'Y from the centre (mm)', kind: 'portY', section: 'General', required: true, make: () => 0 },
  z: mm('Height above the base (mm)', 'General'),
};

const POINT: Table<Vec2> = {
  x: mm('X (mm)', 'General', { kind: 'signedMm', required: true }),
  y: mm('Y (mm)', 'General', { kind: 'signedMm', required: true }),
};

const CONNECTION: Table<Connection> = {
  id: 'hidden',
  from: { label: 'From part', kind: 'partRef', section: 'General', required: true, make: () => '' },
  to: { label: 'To part', kind: 'partRef', section: 'General', required: true, make: () => '' },
  type: { label: 'Discipline', kind: 'select', section: 'General', options: disciplines, make: () => 'electrical' },
  lines: { label: 'Parallel lines', kind: 'number', section: 'General', required: true, make: () => 1 },
  label: { label: 'Description', kind: 'i18n', section: 'General', companions: ['labelI18n'], make: () => '' },
  labelI18n: 'companion',
  route: { label: 'Route', kind: 'select', section: 'General', make: () => 'straight', options: [{ value: 'straight', label: 'Straight' }, { value: 'orthogonal', label: 'Right angles' }] },
  thickness: num('Line width (px)', 'General', 'px', { make: () => 1.5 }),
  style: {
    label: 'Line style', kind: 'select', section: 'General', make: () => 'dashed',
    options: [{ value: 'solid', label: 'Solid' }, { value: 'dashed', label: 'Dashed' }, { value: 'dotted', label: 'Dotted' }, { value: 'dash-dot', label: 'Dash-dot' }],
  },
  layer: 'generic',
  fromEnd: 'generic',
  toEnd: 'generic',
  waypoints: 'generic',
  pipe: 'generic',
  runZ: 'generic',
  labelT: 'generic',
  installedBy: { label: 'Installed by', kind: 'select', section: 'General', make: () => 'stenal', options: [{ value: 'stenal', label: 'Stenal' }, { value: 'client', label: 'Client' }] },
  length: mm('Cable length (mm)', 'General', { make: () => 1000 }),
};

const VARIANT: Table<ProductVariant> = {
  id: 'hidden',
  article: { label: 'Article number', kind: 'text', section: 'General', required: true, make: () => '' },
  power: { label: 'Power', kind: 'power', section: 'General', make: () => 0 },
  voltage: { label: 'Voltage', kind: 'text', section: 'General', make: () => '' },
  current: num('Current (A)', 'General', 'A'),
  price: num('Price (EUR, before VAT)', 'General', 'EUR'),
  loads: list('Other loads', 'General', 'load'),
  properties: list('Properties', 'General', 'property'),
};

type Load = NonNullable<ProductVariant['loads']>[number];
const LOAD: Table<Load> = {
  name: { label: 'Name', kind: 'text', section: 'General', required: true, make: () => '' },
  power: { label: 'Power', kind: 'power', section: 'General', required: true, make: () => 0 },
  voltage: { label: 'Voltage', kind: 'text', section: 'General', make: () => '' },
};

type Screw = NonNullable<ProductTemplate['installScrews']>[number];
const SCREW: Table<Screw> = {
  x: mm('X (mm)', 'General', { required: true }),
  y: mm('Y (mm)', 'General', { required: true }),
  wall: { label: 'Wall', kind: 'select', section: 'General', make: () => 'left', options: [{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }] },
};

const GAP: Table<NonNullable<ProductTemplate['heaterGap']>> = {
  front: mm('Front (mm)', 'General'),
  back: mm('Back (mm)', 'General'),
  left: mm('Left (mm)', 'General'),
  right: mm('Right (mm)', 'General'),
};

const TIERS: Table<NonNullable<ProductTemplate['benchTierHeights']>> = {
  low: mm('Low (mm)', 'General', { required: true, make: () => 450 }),
  medium: mm('Medium (mm)', 'General', { required: true, make: () => 650 }),
  high: mm('High (mm)', 'General', { required: true, make: () => 850 }),
};

const AIR_INLET: Table<NonNullable<ProductTemplate['airInlet']>> = {
  widthMm: mm('Width (mm)', 'General', { required: true }),
  heightMm: mm('Height (mm)', 'General', { required: true }),
  x: mm('X (mm)', 'General'),
  y: mm('Y (mm)', 'General'),
  note: { label: 'Note', kind: 'text', section: 'General', make: () => '' },
};

const WALL_CUT: Table<WallCut> = {
  width: mm('Width (mm)', 'General', { required: true }),
  height: mm('Height (mm)', 'General', { required: true }),
  depth: mm('Depth (mm)', 'General'),
  base: mm('Bottom over the part’s underside (mm)', 'General'),
};

const ROOM_WALL_CUT: Table<RoomWallCut> = {
  width: mm('Width (mm)', 'General', { required: true }),
  height: mm('Height (mm)', 'General', { required: true }),
  depth: mm('Depth (mm)', 'General'),
  base: mm('Bottom over the part’s underside (mm)', 'General'),
};

const WALL_RISERS: Table<WallRisers> = {
  x: mm('Middle across the part (mm)', 'General', { required: true }),
  z: mm('Bottom over the part’s underside (mm)', 'General', { required: true }),
  spacing: mm('Apart (mm)', 'General', { required: true, make: () => 100 }),
  depth: mm('Behind the wall’s face (mm)', 'General', { required: true, make: () => 57 }),
  above: mm('Over the room’s highest top (mm)', 'General', { required: true, make: () => 100 }),
  diameter: mm('Diameter (mm)', 'General', { required: true, make: () => 20 }),
};

const SKIRTING: Table<SkirtingSpec> = {
  boards: { label: 'Boards (mm)', kind: 'mmList', section: 'General', required: true, make: () => [90] },
  gaps: { label: 'Gaps (mm)', kind: 'mmList', section: 'General', required: true, make: () => [] },
  thickness: { label: 'Thickness (mm)', kind: 'mmList', section: 'General', required: true, make: () => [20] },
  clearance: mm('Clearance (mm)', 'General', { required: true }),
};

const TABLES: { readonly [K in ItemKind]: Readonly<Record<string, Slot>> } = {
  airInlet: AIR_INLET,
  connection: CONNECTION,
  gap: GAP,
  load: LOAD,
  part: PART,
  point: POINT,
  port: PORT,
  preparation: PREPARATION,
  product: PRODUCT,
  property: PROPERTY,
  screw: SCREW,
  skirting: SKIRTING,
  tiers: TIERS,
  roomWallCut: ROOM_WALL_CUT,
  variant: VARIANT,
  wallCut: WALL_CUT,
  wallRisers: WALL_RISERS,
};

type Obj = Record<string, unknown>;
const isSpec = (s: Slot | undefined): s is FieldSpec => !!s && typeof s === 'object';

/** One field the editor lists: its key, what it is (null for a key this build
 *  does not know, drawn by its value's type) and its section. */
export interface ListedField {
  key: string;
  spec: FieldSpec | null;
  section: Section;
}

/** The fields an object has, in the order the editor lists them: by section, in
 *  the table's order within one; keys it does not know last, as they come. */
export function listFields(obj: Obj, kind: ItemKind): ListedField[] {
  const table = TABLES[kind], order = Object.keys(table);
  const out: Array<ListedField & { rank: number }> = [];
  Object.keys(obj).forEach((key, i) => {
    if (obj[key] === undefined) return;
    const slot = table[key];
    if (slot === 'hidden' || slot === 'companion') return;
    if (isSpec(slot)) {
      if (slot.derived?.(obj)) return;
      out.push({ key, spec: slot, section: slot.section, rank: SECTIONS.indexOf(slot.section) * 1000 + order.indexOf(key) });
    } else {
      out.push({ key, spec: null, section: 'Other fields', rank: SECTIONS.indexOf('Other fields') * 1000 + i });
    }
  });
  return out.sort((a, b) => a.rank - b.rank).map(({ key, spec, section }) => ({ key, spec, section }));
}

/** The known fields an object lacks that "Add field…" offers, in table order. */
export function addableFields(obj: Obj, kind: ItemKind): Array<{ key: string; spec: FieldSpec }> {
  const out: Array<{ key: string; spec: FieldSpec }> = [];
  for (const [key, slot] of Object.entries(TABLES[kind])) {
    if (!isSpec(slot) || obj[key] !== undefined) continue;
    if (slot.applies && !slot.applies(obj)) continue;
    if (slot.kind === 'drawing') continue; // a drawing is added by importing one
    out.push({ key, spec: slot });
  }
  return out;
}

/** Give an object a known field, at what adding it gives. */
export function addField(obj: Obj, key: string, kind: ItemKind): void {
  const slot = TABLES[kind][key];
  if (isSpec(slot)) obj[key] = slot.make();
}

/** Why a field of this object is shown but cannot be changed (English, the
 *  DICT key), or null: the connection points of a body with a stored 3D model
 *  (`model3d`) — its fittings stand at them, so none is added, removed or moved. */
export function lockedField(obj: Obj, key: string, kind: ItemKind): string | null {
  if (key === 'ports' && (kind === 'product' || kind === 'part') && obj.model3d !== undefined) return MODEL_PORTS_LOCKED;
  return null;
}

/** The reason `lockedField` gives a stored model's connection points. */
export const MODEL_PORTS_LOCKED = 'Its connection points belong to its 3D model: they cannot be added, removed or moved.';

/** Whether a field may be removed: anything but what the object cannot be
 *  without, or what is locked (`lockedField`; with the object given). */
export function removable(key: string, kind: ItemKind, obj?: Obj): boolean {
  const slot = TABLES[kind][key];
  if (obj && lockedField(obj, key, kind)) return false;
  return !(isSpec(slot) && slot.required);
}

/** Remove a field, and the keys its control edits with it. */
export function removeField(obj: Obj, key: string, kind: ItemKind): void {
  if (!removable(key, kind, obj)) return;
  const slot = TABLES[kind][key];
  delete obj[key];
  if (isSpec(slot)) for (const c of slot.companions ?? []) delete obj[c];
}

/** An id not yet among `taken`: `prefix` and the first free number. */
export function freeId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  let n = 0;
  while (used.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

/** The options a select field offers on `obj`: its own, or those the object
 *  gives it — on a room's product (`room`) a preparation's discipline is never
 *  electrical but where it is so already (`preparationTypes`, as the
 *  Preparations tab). */
export function fieldOptions(spec: FieldSpec, obj: Obj, room: boolean): ReadonlyArray<{ value: string; label: string }> {
  return spec.optionsOf?.(obj, room) ?? spec.options ?? [];
}

/** A new item of a list, as "Add" puts it there (a connection between the
 *  product's first two parts; a preparation of a room's product — `room`, by
 *  default `isElecRoom` of it — the first discipline it is offered, never
 *  electrical). */
export function newItem(kind: ItemKind, siblings: readonly Obj[], product: ProductTemplate, room = isElecRoom(product)): Obj {
  const ids = siblings.map((s) => String(s.id ?? ''));
  switch (kind) {
    case 'property': return { key: '', label: '', value: '' };
    case 'preparation': return { id: freeId('prep', ids), type: preparationTypes(room)[0], label: '' };
    case 'port': return { id: freeId('port', ids), name: '', nx: 0, ny: -1, type: 'electrical' };
    case 'connection': {
      const parts = product.parts ?? [];
      return { id: freeId('c', ids), from: parts[0]?.id ?? '', to: parts[1]?.id ?? parts[0]?.id ?? '', lines: 1, type: 'electrical' };
    }
    case 'screw': return { x: 0, y: 0 };
    case 'variant': return { id: freeId('v', ids), article: '', power: 0 };
    case 'load': return { name: '', power: 0 };
    default: return {};
  }
}

/** A property's value as typed: a number when it reads as one exactly, else the text. */
export function propValue(raw: string): string | number {
  const v = raw.trim();
  const n = parseFloat(v);
  return v !== '' && !Number.isNaN(n) && String(n) === v ? n : raw;
}

/** A new part's names: "Part N" in each language. */
function partNames(n: number, trIn: (key: string, lang: Lang) => string): Pick<ProductPart, 'name' | 'nameI18n'> {
  const nameI18n = dictDefaults('Part', trIn, ` ${n}`);
  return { name: nameI18n.en ?? `Part ${n}`, nameI18n };
}

/** Add a part. A single body first becomes its first part, its drawing, size and
 *  body fields (catalogueCopy.ts `bodyFields`) moving onto it (the product keeps
 *  its name, colour, properties and everything else), named as the product is.
 *  The new part comes last, named "Part N" in each language (`trIn`). */
export function addPart(p: ProductTemplate, trIn: (key: string, lang: Lang) => string): ProductPart {
  if (!p.parts?.length) {
    const first: ProductPart = {
      id: 'p0', width: p.width, depth: p.depth, color: p.color, offset: { x: 0, y: 0 },
      ...(p.partName ? { name: p.partName } : p.name ? { name: p.name, ...(p.nameI18n ? { nameI18n: { ...p.nameI18n } } : {}) } : partNames(1, trIn)),
    };
    const src = p as unknown as Obj, dst = first as unknown as Obj;
    for (const [from, to] of bodyFields(p.category)) {
      if (src[from] === undefined) continue;
      dst[to] = src[from];
      delete src[from];
    }
    delete p.partName;
    p.parts = [first];
  }
  const parts = p.parts;
  const right = Math.max(...parts.map((q) => q.offset.x + q.width / 2));
  const part: ProductPart = { id: freeId('p', parts.map((q) => q.id)), ...partNames(parts.length + 1, trIn), width: 600, depth: 500, offset: { x: right + PART_GAP + 300, y: 0 } };
  parts.push(part);
  return part;
}

/** Remove a part and every connection to it; the last part stays. */
export function removePart(p: ProductTemplate, index: number): void {
  const parts = p.parts ?? [];
  if (parts.length <= 1 || !parts[index]) return;
  const [gone] = parts.splice(index, 1);
  if (p.connections) p.connections = p.connections.filter((c) => c.from !== gone.id && c.to !== gone.id);
}

/** Gap between a new part and the rightmost of the others. */
export const PART_GAP = 600;

/**
 * Finish an edit: what the product cannot be saved without, else null. The one
 * thing a save changes on its own is a multi-part product's width and depth:
 * its parts' bounding box, as they stand (docs/claude/products.md, *Editing a
 * product*).
 */
export function finishEdit(p: ProductTemplate): string | null {
  if (!String(p.name ?? '').trim()) return 'Please enter a product name.';
  const parts = p.parts ?? [];
  const sizes = parts.length ? parts : [p];
  if (sizes.some((s) => !(s.width > 0) || !(s.depth > 0))) return 'Every part needs a valid width and depth.';
  if (parts.length) {
    const xs = parts.flatMap((q) => [q.offset.x - q.width / 2, q.offset.x + q.width / 2]);
    const ys = parts.flatMap((q) => [q.offset.y - q.depth / 2, q.offset.y + q.depth / 2]);
    p.width = Math.max(...xs) - Math.min(...xs);
    p.depth = Math.max(...ys) - Math.min(...ys);
  }
  return null;
}
